import { faroCoreApiClient } from "@api/faro-core-api-client";
import { BaseCompanyProjectIdProps } from "@custom-types/base-types";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { getProjectByIdSelector } from "@store/projects/projects-selector";
import { useAppSelector } from "@store/store-helper";
import { sentryCaptureError } from "@utils/sentry-utils";
import { useEffect, useState } from "react";

/**
 * URL route params for the snapshot redirect check.
 * Optional since they may be undefined during initial render.
 */
type UseSnapshotRedirectProps = Partial<BaseCompanyProjectIdProps>;

interface UseSnapshotRedirectReturn {
  /** True while verifying if `projectId` is a snapshot, or while redirecting. */
  isRedirecting: boolean;
}

/**
 * Checks if the `projectId` in the URL belongs to a snapshot rather than a project.
 * If so, redirects to the parent project's snapshots tab with the snapshot ID as a query param:
 * `/{companyId}/projects/{parentProjectId}/snapshots?snapshotId={snapshotId}`
 *
 * Skips the API check when the project is already in the Redux store
 * (e.g. user navigated from the projects list), since known projects can't be snapshots.
 */
export function useSnapshotRedirect({
  companyId,
  projectId,
}: UseSnapshotRedirectProps): UseSnapshotRedirectReturn {
  const { navigateToSnapshot } = useAppNavigation();
  const existingProject = useAppSelector(getProjectByIdSelector(projectId));
  // Initialize lazily so the optimized path (project already in Redux) skips
  // the initial null render in the consumer.
  const [isRedirecting, setIsRedirecting] = useState(
    () => Boolean(companyId && projectId) && !existingProject
  );

  /**
   * Check if the projectId is a snapshotId and redirect to the snapshot page if it is,
   * otherwise do nothing and let the page render as normal.
   */
  useEffect(() => {
    if (!companyId || !projectId || existingProject) {
      setIsRedirecting(false);
      return;
    }

    let isCancelled = false;

    async function checkIfSnapshot(
      safeCompanyId: string,
      safeProjectId: string
    ): Promise<void> {
      try {
        const snapshotDetails =
          await faroCoreApiClient.projects.getSnapshotDetails({
            companyId: safeCompanyId,
            snapshotId: safeProjectId,
          });

        if (isCancelled) {
          return;
        }

        // The same endpoint serves both regular projects and snapshots.
        // Only snapshots have `originalProjectId`; regular projects don't.
        if (!snapshotDetails.originalProjectId) {
          setIsRedirecting(false);
          return;
        }

        navigateToSnapshot({
          companyId: safeCompanyId,
          parentProjectId: snapshotDetails.originalProjectId,
          snapshotId: safeProjectId,
        });
      } catch (error) {
        if (isCancelled) {
          return;
        }

        // Handle silently: a failure here just means we can't determine if the ID
        // is a snapshot, so the page falls through to its normal not-found flow.
        // Still log to Sentry so unexpected failures remain visible.
        sentryCaptureError({
          error,
          title: "Failed to verify if the project is a snapshot.",
        });

        setIsRedirecting(false);
      }
    }

    void checkIfSnapshot(companyId, projectId);

    return () => {
      isCancelled = true;
    };
  }, [companyId, existingProject, navigateToSnapshot, projectId]);

  return { isRedirecting };
}
